import { FaUsers } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ArticleIcon from '@mui/icons-material/Article';
import FlagIcon from '@mui/icons-material/Flag';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MyLogo from '../Assets/logo.svg';
import CollapsedLogo from '../Assets/collapsed-logo.svg';
import StyledSidebarLayout from './style';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const ModuleEnumType = Object.freeze({
   DASHBOARD: 'dashboard',
   USER: 'user',
   SERVICE_PROVIDER: 'service_provider',
   QUOTATION: 'quotation',
   ESTIMATE: 'estimate',
   PROPOSAL: 'proposal',
   HOUR_RATE: 'hour_rate',
   MILESTONE: 'milestone',
   CONTRACT: 'contract',
   BANK_ACCOUNT: 'bank_account',
   TECH_STACK: 'tech_stack',
   ROLE_PERMISSION: 'role_permission',
});

function SideNav() {
   const location = useLocation();
   const [isCollapsed, setIsCollapsed] = useState(false);
   const [hourlyRateOpen, setHourlyRateOpen] = useState(false);
   const [quotationOpen, setQuotationOpen] = useState(false);
   const [contractOpen, setContractOpen] = useState(false);
   const { user } = useSelector((state) => state.authState);
   const hasShownModuleError = useRef(false);

   // Detect sidebar collapse from body class
   useEffect(() => {
      const observer = new MutationObserver(() => {
         setIsCollapsed(document.body.classList.contains('sidebar-collapse'));
      });

      observer.observe(document.body, {
         attributes: true,
         attributeFilter: ['class'],
      });

      return () => observer.disconnect();
   }, []);

   // Keep subnav open if on a subroute and handle initial load
   useEffect(() => {
      if (location.pathname.startsWith('/hourly-rate')) {
         setHourlyRateOpen(true);
      } else {
         // Close dropdown when navigating away from hourly-rate routes
         setHourlyRateOpen(false);
      }
   }, [location.pathname]);

   const collapseSidebarIfMobile = () => {
      if (window.innerWidth <= 991) {
         document.body.classList.add('sidebar-collapse');
      }
   };

   const isActive = (path) =>
      path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

   // Helper to check if any path in a list is active (useful for grouped menus like Quotation)
   const isAnyActive = (paths = []) =>
      paths.some((p) => (p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)));

   // Load user role and expose an isSuperAdmin helper
   const [userRole, setUserRole] = useState(null);
   useEffect(() => {
      const extractRole = (parsed) => {
         if (!parsed) return null;
         // if it's a plain string
         if (typeof parsed === 'string') return parsed;

         // direct role field
         if (parsed.role) {
            if (Array.isArray(parsed.role)) return parsed.role[0]?.name || parsed.role[0];
            return parsed.role;
         }

         // nested user object
         const userObj =
            parsed.user || parsed.state?.user || parsed.auth?.user || parsed.data?.user;
         if (userObj) {
            if (userObj.role) {
               if (Array.isArray(userObj.role)) return userObj.role[0]?.name || userObj.role[0];
               return userObj.role;
            }
         }

         // sometimes role can be inside an array of roles
         if (parsed.roles && Array.isArray(parsed.roles)) {
            return parsed.roles[0]?.name || parsed.roles[0];
         }

         return null;
      };

      try {
         const keysToCheck = ['role', 'user', 'state'];
         for (const key of keysToCheck) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            let parsed;
            try {
               parsed = JSON.parse(raw);
            } catch (e) {
               parsed = raw;
            }
            const role = extractRole(parsed);
            if (role) {
               setUserRole(role);
               return;
            }
         }

         // final fallback: try all keys in localStorage to find a role
         for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            const raw = localStorage.getItem(k);
            if (!raw) continue;
            let parsed;
            try {
               parsed = JSON.parse(raw);
            } catch (e) {
               parsed = raw;
            }
            const role = extractRole(parsed);
            if (role) {
               setUserRole(role);
               return;
            }
         }
      } catch (err) {
         // ignore parsing errors
      }
   }, []);

   const isSuperAdmin = userRole && userRole.toString().toLowerCase().includes('super');

   // Helper for subnav active state (exact match)
   const isSubActive = (subPath) => subPath.includes(location.pathname);

   // Custom style for selected subnav
   const subNavSelectedStyle = {
      fontWeight: 500,
      fontSize: 14,
      color: '#fff',
      paddingLeft: 40,
      background: '#3F30FF',
      borderRadius: '8px',
      border: '2px solid #3F30FF',
      boxShadow: '0 2px 8px 0 rgba(63,48,255,0.10)',
   };

   // Custom style for unselected subnav
   const subNavUnselectedStyle = {
      fontWeight: 400,
      fontSize: 14,
      color: '#b8b8b8',
      paddingLeft: 40,
      background: 'none',
      borderRadius: '8px',
      border: '2px solid transparent',
   };

   // Toggle Hourly Rate dropdown with proper handling
   const handleHourlyRateToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setHourlyRateOpen((prevState) => !prevState);
   };

   // Toggle Quotation dropdown with proper handling
   const handleQuotationToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setQuotationOpen((prev) => !prev);
   };

   // Toggle Contract dropdown with proper handling
   const handleContractToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setContractOpen((prev) => !prev);
   };

   const hasModuleAccess = (moduleName = '') => {
      const modules = user.roleDetails?.modules;

      if (!modules) {
         if (!hasShownModuleError.current) {
            hasShownModuleError.current = true;
            toast.error('Module access not found!');
         }
         return false;
      }

      hasShownModuleError.current = false;
      return modules.some((module) => module.name === moduleName);
   };

   // Keep Quotation subnav open if on a subroute and handle initial load
   useEffect(() => {
      if (isAnyActive(['/quotation', '/discount', '/services-for-sell'])) {
         setQuotationOpen(true);
      } else {
         setQuotationOpen(false);
      }
   }, [location.pathname]);

   // Keep Contract subnav open if on a subroute and handle initial load
   useEffect(() => {
      if (isAnyActive(['/contract', '/contract-type'])) {
         setContractOpen(true);
      } else {
         setContractOpen(false);
      }
   }, [location.pathname]);

   // Estimate active across list and edit routes
   const isEstimateActive = isAnyActive(['/estimate', '/estimation-edit']);

   return (
      <StyledSidebarLayout>
         <aside
            className="main-sidebar sidebar-dark-primary elevation-4"
            style={{ minHeight: '100vh', background: '#232946' }}
         >
            <Link
               to="/"
               className="brand-link"
               style={{ background: '#232946', textAlign: 'center' }}
            >
               <img
                  src={isCollapsed ? CollapsedLogo : MyLogo}
                  alt="Logo"
                  className="logo"
                  style={{
                     width: isCollapsed ? '40px' : '150px',
                     margin: '0 auto',
                  }}
               />
            </Link>
            <div className="sidebar" style={{ background: '#232946' }}>
               <nav className="mt-2">
                  <ul
                     className="nav nav-pills nav-sidebar flex-column"
                     data-widget="treeview"
                     role="menu"
                     data-accordion="false"
                  >
                     {/* Dashboard */}(
                     <li className="nav-item">
                        <Link
                           to="/dashboard"
                           className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                           onClick={collapseSidebarIfMobile}
                           style={{
                              fontWeight: 500,
                              fontSize: 16,
                              color: '#fff',
                           }}
                        >
                           <AssessmentIcon className="icon-size" style={{ marginRight: 10 }} />
                           Dashboard
                        </Link>
                     </li>
                     ){/* Hourly Rate with nested menu */}
                     {/* {hasModuleAccess(ModuleEnumType.HOUR_RATE) && (
                        <li
                           className={`nav-item has-treeview ${hourlyRateOpen ? 'menu-open' : ''}`}
                        >
                           <div
                              className={`nav-link ${isActive('/hourly-rate') ? 'active' : ''}`}
                              onClick={handleHourlyRateToggle}
                              style={{
                                 cursor: 'pointer',
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                                 display: 'flex',
                                 alignItems: 'center',
                                 userSelect: 'none',
                              }}
                           >
                              <EngineeringIcon className="icon-size" style={{ marginRight: 10 }} />
                              <span style={{ flex: 1 }}>Hourly Rate</span>
                              <i
                                 className={`right fas fa-angle-left ${
                                    hourlyRateOpen ? 'rotate-icon' : ''
                                 }`}
                                 style={{
                                    marginLeft: 'auto',
                                    fontSize: 18,
                                    transform: hourlyRateOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                 }}
                              ></i>
                           </div>
                           <ul
                              className={`nav nav-treeview ${hourlyRateOpen ? 'show' : 'hide'}`}
                              style={{
                                 display: hourlyRateOpen ? 'block' : 'none',
                                 background: '#232946',
                                 transition: 'all 0.2s ease-in-out',
                              }}
                           >
                              <li className="nav-item">
                                 <Link
                                    to="/hourly-rate/tech-stack"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive([
                                          '/hourly-rate/tech-stack',
                                          '/hourly-rate/tech-stack-add',
                                       ])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>Tech Stack</span>
                                 </Link>
                              </li>
                              <li className="nav-item">
                                 <Link
                                    to="/hourly-rate/qa-rate-list"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive([
                                          '/hourly-rate/qa-rate-list',
                                          '/hourly-rate/add-qa-rate',
                                       ])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>QA, PM, DevOPS</span>
                                 </Link>
                              </li>
                              <li className="nav-item">
                                 <Link
                                    to="/hourly-rate/seo-rate-list"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive([
                                          '/hourly-rate/seo-rate-list',
                                          '/hourly-rate/add-seo-rate',
                                       ])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>SEO</span>
                                 </Link>
                              </li>
                           </ul>
                        </li>
                     )} */}
                     {/* Tech Stacks */}
                     {hasModuleAccess(ModuleEnumType.TECH_STACK) && (
                        <li className="nav-item">
                           <Link
                              to="/tech-stack/techstacklist"
                              className={`nav-link ${
                                 isActive('/tech-stack/techstacklist') ? 'active' : ''
                              }`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <GroupWorkIcon className="icon-size" style={{ marginRight: 10 }} />
                              Tech Stacks
                           </Link>
                        </li>
                     )}
                     {/* Service Providers */}
                     {hasModuleAccess(ModuleEnumType.SERVICE_PROVIDER) && (
                        <li className="nav-item">
                           <Link
                              to="/service-providers"
                              className={`nav-link ${isActive('/service-providers') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <FaUsers style={{ marginRight: 10 }} />
                              Service Providers
                           </Link>
                        </li>
                     )}
                     {/* Bank Details */}
                     {hasModuleAccess(ModuleEnumType.BANK_ACCOUNT) && (
                        <li className="nav-item">
                           <Link
                              to="/bank"
                              className={`nav-link ${isActive('/bank') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <FaUsers style={{ marginRight: 10 }} />
                              Bank Accounts
                           </Link>
                        </li>
                     )}
                     {hasModuleAccess(ModuleEnumType.ROLE_PERMISSION) && (
                        <li className="nav-item">
                           <Link
                              to="/role-permissions"
                              className={`nav-link ${isActive('/role-permissions') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <AssessmentIcon className="icon-size" style={{ marginRight: 10 }} />
                              Role Permissions
                           </Link>
                        </li>
                     )}
                     {/* Users */}
                     {hasModuleAccess(ModuleEnumType.USER) && (
                        <li className="nav-item">
                           <Link
                              to="/user"
                              className={`nav-link ${isActive('/user') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <FaUsers className="icon-size" style={{ marginRight: 10 }} />
                              Users
                           </Link>
                        </li>
                     )}
                     {/* Opportunities */}
                     <li className="nav-item">
                        <Link
                           to="/opportunities"
                           className={`nav-link ${isActive('/opportunities') ? 'active' : ''}`}
                           onClick={collapseSidebarIfMobile}
                           style={{
                              fontWeight: 500,
                              fontSize: 16,
                              color: '#fff',
                           }}
                        >
                           <LeaderboardIcon className="icon-size" style={{ marginRight: 10 }} />
                           Opportunities
                        </Link>
                     </li>
                     {/* Estimate */}
                     {hasModuleAccess(ModuleEnumType.ESTIMATE) && (
                        <li className="nav-item">
                           <Link
                              to="/estimate"
                              className={`nav-link ${isEstimateActive ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <DocumentScannerIcon
                                 className="icon-size"
                                 style={{ marginRight: 10 }}
                              />
                              Estimate
                           </Link>
                        </li>
                     )}
                     {/* Quotation */}
                     {hasModuleAccess(ModuleEnumType.QUOTATION) && (
                        <li className={`nav-item has-treeview ${quotationOpen ? 'menu-open' : ''}`}>
                           <div
                              className={`nav-link ${
                                 isAnyActive(['/quotation', '/discount', '/services-for-sell'])
                                    ? 'active'
                                    : ''
                              }`}
                              onClick={handleQuotationToggle}
                              style={{
                                 cursor: 'pointer',
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                                 display: 'flex',
                                 alignItems: 'center',
                                 userSelect: 'none',
                              }}
                           >
                              <PostAddIcon className="icon-size" style={{ marginRight: 10 }} />
                              <span style={{ flex: 1 }}>Quotation</span>
                              <i
                                 className={`right fas fa-angle-left ${
                                    quotationOpen ? 'rotate-icon' : ''
                                 }`}
                                 style={{
                                    marginLeft: 'auto',
                                    fontSize: 18,
                                    transform: quotationOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                 }}
                              ></i>
                           </div>
                           <ul
                              className={`nav nav-treeview ${quotationOpen ? 'show' : 'hide'}`}
                              style={{
                                 display: quotationOpen ? 'block' : 'none',
                                 background: '#232946',
                                 transition: 'all 0.2s ease-in-out',
                              }}
                           >
                              <li className="nav-item">
                                 <Link
                                    to="/quotation"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive(['/quotation', '/quotation-add'])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>List</span>
                                 </Link>
                              </li>
                              {isSuperAdmin && (
                                 <li className="nav-item">
                                    <Link
                                       to="/discount"
                                       className={`nav-link`}
                                       onClick={collapseSidebarIfMobile}
                                       style={
                                          isSubActive(['/discount', '/discount-add'])
                                             ? subNavSelectedStyle
                                             : subNavUnselectedStyle
                                       }
                                    >
                                       <GroupWorkIcon
                                          className="icon-size"
                                          style={{ fontSize: 18, marginRight: 8 }}
                                       />
                                       <span style={{ fontSize: 14 }}>Discount</span>
                                    </Link>
                                 </li>
                              )}
                              {isSuperAdmin && (
                                 <li className="nav-item">
                                    <Link
                                       to="/services-for-sell"
                                       className={`nav-link`}
                                       onClick={collapseSidebarIfMobile}
                                       style={
                                          isSubActive([
                                             '/services-for-sell',
                                             '/services-for-sell-add',
                                          ])
                                             ? subNavSelectedStyle
                                             : subNavUnselectedStyle
                                       }
                                    >
                                       <GroupWorkIcon
                                          className="icon-size"
                                          style={{ fontSize: 18, marginRight: 8 }}
                                       />
                                       <span style={{ fontSize: 14 }}>Services</span>
                                    </Link>
                                 </li>
                              )}
                           </ul>
                        </li>
                     )}
                     {/* Milestones */}
                     {hasModuleAccess(ModuleEnumType.MILESTONE) && (
                        <li className="nav-item">
                           <Link
                              to="/milestones"
                              className={`nav-link ${isActive('/milestone') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <FlagIcon className="icon-size" style={{ marginRight: 10 }} />
                              Milestones
                           </Link>
                        </li>
                     )}
                     {/* Proposal */}
                     {hasModuleAccess(ModuleEnumType.PROPOSAL) && (
                        <li className="nav-item">
                           <Link
                              to="/proposal"
                              className={`nav-link ${isActive('/proposal') ? 'active' : ''}`}
                              onClick={collapseSidebarIfMobile}
                              style={{
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                              }}
                           >
                              <PictureAsPdfIcon className="icon-size" style={{ marginRight: 10 }} />
                              Proposal
                           </Link>
                        </li>
                     )}
                     {/* Contract */}
                     {hasModuleAccess(ModuleEnumType.CONTRACT) && (
                        <li className={`nav-item has-treeview ${contractOpen ? 'menu-open' : ''}`}>
                           <div
                              className={`nav-link ${
                                 isAnyActive(['/contract', '/contract-type']) ? 'active' : ''
                              }`}
                              onClick={handleContractToggle}
                              style={{
                                 cursor: 'pointer',
                                 fontWeight: 500,
                                 fontSize: 16,
                                 color: '#fff',
                                 display: 'flex',
                                 alignItems: 'center',
                                 userSelect: 'none',
                              }}
                           >
                              <ArticleIcon className="icon-size" style={{ marginRight: 10 }} />
                              <span style={{ flex: 1 }}>Contract</span>
                              <i
                                 className={`right fas fa-angle-left ${
                                    contractOpen ? 'rotate-icon' : ''
                                 }`}
                                 style={{
                                    marginLeft: 'auto',
                                    fontSize: 18,
                                    transform: contractOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                 }}
                              ></i>
                           </div>
                           <ul
                              className={`nav nav-treeview ${contractOpen ? 'show' : 'hide'}`}
                              style={{
                                 display: contractOpen ? 'block' : 'none',
                                 background: '#232946',
                                 transition: 'all 0.2s ease-in-out',
                              }}
                           >
                              <li className="nav-item">
                                 <Link
                                    to="/contract"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive(['/contract', '/contract-add'])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>List</span>
                                 </Link>
                              </li>
                              <li className="nav-item">
                                 <Link
                                    to="/contract-type/list"
                                    className={`nav-link`}
                                    onClick={collapseSidebarIfMobile}
                                    style={
                                       isSubActive(['/contract-type/list', '/contract-type/add'])
                                          ? subNavSelectedStyle
                                          : subNavUnselectedStyle
                                    }
                                 >
                                    <GroupWorkIcon
                                       className="icon-size"
                                       style={{ fontSize: 18, marginRight: 8 }}
                                    />
                                    <span style={{ fontSize: 14 }}>Contract Types</span>
                                 </Link>
                              </li>
                           </ul>
                        </li>
                     )}
                  </ul>
               </nav>
            </div>
         </aside>
      </StyledSidebarLayout>
   );
}

export default SideNav;
